import { Form } from 'formik';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikTextField,
  FormikNumericTextField,
  P2PProviderSelect,
  ShopSelect,
  TraderSelect,
  FormikDialog,
  FormikCheckbox,
  FormikSelect,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { TelegramFlow, TelegramUser } from 'types';
import { formUtils } from 'utils';

export enum TelegramFlowContext {
  Trader = 'trader',
  Shop = 'shop',
  P2PProvider = 'p2p-provider',
}

type Values = {
  traderId?: string;
  shopId?: string;
  p2pProviderId?: string;
  telegramUserId?: string;
  chatId: string;
  enabled: boolean;
  payinDisputeTemplate: string | null;
  defaultPayinDisputeTemplate?: boolean;
  payinCancelTemplate: string | null;
  defaultPayinCancelTemplate?: boolean;
  payinRequireReceipt: boolean;
  payinHideActions: boolean;
  payinSendDisputesMessage: boolean;
};

type Props = DialogProps<CloseFormikDialogResult<Values>> & {
  context: TelegramFlowContext;
  telegramUsers: TelegramUser[];
};

export const TelegramFlowDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  context,
  telegramUsers,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow.dialog',
  });
  const { t: tCommon } = useTranslation();

  const title = useMemo(
    () => (data ? t('edit_title') : t('create_title')),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      traderId: '',
      shopId: '',
      p2pProviderId: '',
      telegramUserId: '',
      chatId: '',
      enabled: true,
      payinDisputeTemplate: '',
      defaultPayinDisputeTemplate: true,
      payinCancelTemplate: '',
      defaultPayinCancelTemplate: true,
      payinHideActions: false,
      payinRequireReceipt: false,
      payinSendDisputesMessage: false,
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const telegramUsersOptions = useMemo(
    () => formUtils.getOptions(telegramUsers),
    [telegramUsers],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        traderId:
          context === TelegramFlowContext.Trader
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        shopId:
          context === TelegramFlowContext.Shop
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        p2pProviderId:
          context === TelegramFlowContext.P2PProvider
            ? Yup.string().required(tCommon('errors.required'))
            : Yup.string().nullable().optional(),
        telegramUserId: Yup.string().nullable().optional(),
        chatId: Yup.string().required(tCommon('errors.required')),
        enabled: Yup.boolean(),
        defaultPayinDisputeTemplate: Yup.boolean(),
        defaultPayinCancelTemplate: Yup.boolean(),
        payinHideActions: Yup.boolean(),
        payinSendDisputesMessage: Yup.boolean(),
        payinRequireReceipt:
          context === TelegramFlowContext.Shop
            ? Yup.boolean()
            : Yup.boolean().nullable().optional(),
        payinDisputeTemplate: Yup.string().when('defaultPayinDisputeTemplate', {
          is: false,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        payinCancelTemplate: Yup.string().when('defaultPayinCancelTemplate', {
          is: false,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [context, tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    const flow = data as any as TelegramFlow;
    setInitialValues({
      ..._initialValues,
      ...data,
      defaultPayinDisputeTemplate: !flow?.payinDisputeTemplate, // TODO: fix typings?
      defaultPayinCancelTemplate: !flow?.payinCancelTemplate,
      payinHideActions: !!flow?.payinHideActions,
      payinRequireReceipt: !!flow?.payinRequireReceipt,
      payinSendDisputesMessage: !!flow?.payinSendDisputesMessage,
    });
  }, [data, getInitialValues]);

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        const values = {
          chatId: result.data.values.chatId,
          enabled: result.data.values.enabled,
          payinDisputeTemplate: result.data.values.defaultPayinDisputeTemplate
            ? null
            : result.data.values.payinDisputeTemplate,
          payinCancelTemplate: result.data.values.defaultPayinCancelTemplate
            ? null
            : result.data.values.payinCancelTemplate,
          traderId: result.data.values.traderId || undefined,
          shopId: result.data.values.shopId || undefined,
          p2pProviderId: result.data.values.p2pProviderId || undefined,
          telegramUserId: result.data.values.telegramUserId || null,
          payinHideActions: result.data.values.payinHideActions,
          payinRequireReceipt: result.data.values.payinRequireReceipt,
          payinSendDisputesMessage: result.data.values.payinSendDisputesMessage,
        };
        onClose({
          ok: true,
          data: {
            ...result.data,
            values,
          },
        });
      } else {
        onClose(result);
      }
    },
    [onClose],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
      }}
      formikProps={{
        initialValues,
        enableReinitialize: true,
        validationSchema,
      }}
    >
      {({ formik }) => (
        <Form>
          <FormControls>
            <div>
              <FormikCheckbox name="enabled" label={t('fields.enabled')} />
              <FormikCheckbox
                name="payinSendDisputesMessage"
                label={t('fields.send_disputes_message')}
              />
              <FormikCheckbox
                name="payinHideActions"
                label={t('fields.payin_hide_actions')}
                disabled={
                  context === TelegramFlowContext.Shop &&
                  !formik.values.payinSendDisputesMessage
                }
              />
              {context === TelegramFlowContext.Shop && (
                <FormikCheckbox
                  name="payinRequireReceipt"
                  label={t('fields.payin_require_receipt')}
                />
              )}
            </div>
            {context === TelegramFlowContext.Trader && (
              <TraderSelect name="traderId" label={t('fields.trader')} />
            )}
            {context === TelegramFlowContext.Shop && (
              <ShopSelect name="shopId" label={t('fields.shop')} />
            )}
            {context === TelegramFlowContext.P2PProvider && (
              <P2PProviderSelect
                name="p2pProviderId"
                label={t('fields.p2p_provider')}
                ignoreTradeMethodsIds
              />
            )}
            <FormikNumericTextField
              name="chatId"
              label={t('fields.chat_id')}
              required
            />
            {context === TelegramFlowContext.Shop && (
              <FormikSelect
                name="telegramUserId"
                label={t('fields.telegram_user')}
                helperText={t('fields.telegram_user_helper')}
                options={telegramUsersOptions}
                noneOption
              />
            )}
            <FormikCheckbox
              name="defaultPayinDisputeTemplate"
              label={t('fields.default_template')}
            />
            {!formik.values.defaultPayinDisputeTemplate && (
              <FormikTextField
                name="payinDisputeTemplate"
                label={t('fields.template')}
                helperText={
                  context === TelegramFlowContext.Shop
                    ? t('fields.template_shop_helper')
                    : undefined
                }
                required
                multiline
                rows={3}
              />
            )}
            {context === TelegramFlowContext.Shop && (
              <Fragment>
                <FormikCheckbox
                  name="defaultPayinCancelTemplate"
                  label={t('fields.default_cancel_template')}
                />
                {!formik.values.defaultPayinCancelTemplate && (
                  <FormikTextField
                    name="payinCancelTemplate"
                    label={t('fields.cancel_template')}
                    required
                    multiline
                    rows={3}
                  />
                )}
              </Fragment>
            )}
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
